import React, { useState, useMemo } from 'react';
import { Card, Table, Form, Button, Row, Col, Badge, Spinner, Modal } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client';
import { GET_EXPENSES, CREATE_EXPENSE, DELETE_EXPENSE, UPDATE_EXPENSE_STATUS, UPDATE_EXPENSE } from '../../graphql/operations';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import AdminPagination from './AdminPagination';

const PREDEFINED_CATEGORIES = [
  'Office Supplies',
  'Maintenance',
  'Utilities',
  'Travel',
  'Marketing',
  'Software/Subscriptions',
  'Raw Materials',
  'Salary/Wages',
  'Other'
];

interface ExpenseTrackerProps {
  triggerToast: (message: string, variant?: 'success' | 'danger' | 'warning' | 'info') => void;
}

const ExpenseTracker: React.FC<ExpenseTrackerProps> = ({ triggerToast }) => {
  const [filterPeriod, setFilterPeriod] = useState('monthly');
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear().toString());
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Determine date ranges based on filter
  const dateRanges = useMemo(() => {
    let start = '';
    let end = '';

    if (filterPeriod === 'daily') {
      if (selectedDate) {
        const d = new Date(selectedDate);
        start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
        end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();
      }
    } else if (filterPeriod === 'weekly') {
      const now = new Date();
      const firstDay = now.getDate() - now.getDay();
      start = new Date(now.getFullYear(), now.getMonth(), firstDay).toISOString();
      end = new Date(now.getFullYear(), now.getMonth(), firstDay + 6, 23, 59, 59, 999).toISOString();
    } else if (filterPeriod === 'monthly') {
      if (selectedMonth) {
        const [year, month] = selectedMonth.split('-');
        const y = parseInt(year);
        const m = parseInt(month) - 1;
        start = new Date(y, m, 1).toISOString();
        end = new Date(y, m + 1, 0, 23, 59, 59, 999).toISOString();
      }
    } else if (filterPeriod === 'yearly') {
      if (selectedYear) {
        const y = parseInt(selectedYear);
        start = new Date(y, 0, 1).toISOString();
        end = new Date(y, 11, 31, 23, 59, 59, 999).toISOString();
      }
    } else if (filterPeriod === 'custom') {
      start = customStartDate ? new Date(customStartDate).toISOString() : '';
      end = customEndDate ? new Date(customEndDate + 'T23:59:59').toISOString() : '';
    }

    return { startDate: start, endDate: end };
  }, [filterPeriod, selectedDate, selectedMonth, selectedYear, customStartDate, customEndDate]);

  const { data, loading, refetch } = useQuery(GET_EXPENSES, {
    variables: {
      startDate: dateRanges.startDate || null,
      endDate: dateRanges.endDate || null,
    },
    fetchPolicy: 'network-only',
  });

  const [createExpenseMutation] = useMutation(CREATE_EXPENSE, {
    onCompleted: () => {
      refetch();
      setShowAddModal(false);
      setForm({ description: '', amount: '', category: PREDEFINED_CATEGORIES[0], paymentStatus: 'paid', paymentMode: 'Cash', date: new Date().toISOString().slice(0, 10) });
      triggerToast('Expense added successfully!', 'success');
    },
    onError: (err) => {
      triggerToast(err.message || 'Failed to add expense', 'danger');
    }
  });

  const [deleteExpenseMutation] = useMutation(DELETE_EXPENSE, {
    onCompleted: () => {
      refetch();
      triggerToast('Expense deleted successfully!', 'success');
    },
    onError: (err) => {
      triggerToast(err.message || 'Failed to delete expense', 'danger');
    }
  });

  const [updateExpenseStatusMutation] = useMutation(UPDATE_EXPENSE_STATUS, {
    onCompleted: () => {
      refetch();
      triggerToast('Expense updated successfully!', 'success');
    },
    onError: (err) => {
      triggerToast(err.message || 'Failed to update expense', 'danger');
    }
  });

  const handleStatusChange = (id: string, paymentStatus: string) => {
    updateExpenseStatusMutation({ variables: { id, paymentStatus } });
  };

  const handleModeChange = (id: string, paymentMode: string) => {
    updateExpenseStatusMutation({ variables: { id, paymentMode } });
  };

  const userRole = localStorage.getItem('role');
  const isAdminManager = userRole === 'admin-manager';

  const [updateExpenseMutation] = useMutation(UPDATE_EXPENSE, {
    onCompleted: () => {
      refetch();
      setShowAddModal(false);
      setIsEditing(false);
      setSelectedExpenseId(null);
      setForm({ description: '', amount: '', category: PREDEFINED_CATEGORIES[0], paymentStatus: 'paid', paymentMode: 'Cash', date: new Date().toISOString().slice(0, 10) });
      triggerToast('Expense updated successfully!', 'success');
    },
    onError: (err) => {
      triggerToast(err.message || 'Failed to update expense', 'danger');
    }
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedExpenseId, setSelectedExpenseId] = useState<string | null>(null);
  const [form, setForm] = useState({
    description: '',
    amount: '',
    category: PREDEFINED_CATEGORIES[0],
    paymentStatus: 'paid',
    paymentMode: 'Cash',
    date: new Date().toISOString().slice(0, 10)
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 15;

  const [statusFilter, setStatusFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  const [sortOption, setSortOption] = useState('date-desc');

  const rawExpenses = data?.expenses || [];
  
  const expenses = useMemo(() => {
    let filtered = [...rawExpenses];
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(exp => exp.paymentStatus === statusFilter);
    }
    
    if (modeFilter !== 'all') {
      filtered = filtered.filter(exp => (exp.paymentMode || '') === modeFilter);
    }
    
    filtered.sort((a, b) => {
      if (sortOption === 'date-desc') {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      }
      if (sortOption === 'date-asc') {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }
      if (sortOption === 'amount-desc') {
        return b.amount - a.amount;
      }
      if (sortOption === 'amount-asc') {
        return a.amount - b.amount;
      }
      return 0;
    });
    
    return filtered;
  }, [rawExpenses, statusFilter, modeFilter, sortOption]);
  
  // Calculate total
  const totalExpenses = expenses.reduce((sum: number, exp: any) => sum + (exp.amount || 0), 0);

  // Pagination
  const totalPages = Math.ceil(expenses.length / ITEMS_PER_PAGE);
  const paginatedExpenses = expenses.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleEditClick = (exp: any) => {
    setIsEditing(true);
    setSelectedExpenseId(exp.id);
    setForm({
      description: exp.description,
      amount: exp.amount.toString(),
      category: exp.category,
      paymentStatus: exp.paymentStatus || 'paid',
      paymentMode: exp.paymentMode || 'Cash',
      date: new Date(exp.date).toISOString().slice(0, 10)
    });
    setShowAddModal(true);
  };

  const handleAddExpense = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.description || !form.amount || !form.category || !form.date) {
      triggerToast('Please fill all required fields', 'warning');
      return;
    }
    
    const inputData = {
      description: form.description,
      amount: parseFloat(form.amount),
      category: form.category,
      date: new Date(form.date).toISOString(),
      paymentStatus: form.paymentStatus,
      paymentMode: form.paymentMode,
    };
    
    if (isEditing && selectedExpenseId) {
      updateExpenseMutation({
        variables: {
          id: selectedExpenseId,
          input: inputData
        }
      });
    } else {
      createExpenseMutation({
        variables: {
          input: inputData
        }
      });
    }
  };

  const handleDeleteExpense = (id: string) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      deleteExpenseMutation({ variables: { id } });
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text(`Expense Report - ${filterPeriod.toUpperCase()}`, 14, 15);
    doc.setFontSize(11);
    doc.text(`Total: ₹${totalExpenses.toFixed(2)}`, 14, 25);
    
    const tableData = expenses.map((exp: any) => [
      new Date(exp.date).toLocaleDateString(),
      exp.description,
      exp.category,
      `Rs. ${exp.amount.toFixed(2)}`,
      exp.paymentStatus === 'pending' ? 'Pending' : 'Paid',
      exp.paymentMode || '-',
      exp.createdBy?.username || 'Unknown'
    ]);

    autoTable(doc, {
      startY: 30,
      head: [['Date', 'Description', 'Category', 'Amount', 'Status', 'Mode', 'Created By']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [13, 110, 253] }
    });

    doc.save(`expenses_${filterPeriod}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const exportToExcel = () => {
    const excelData = expenses.map((exp: any) => ({
      Date: new Date(exp.date).toLocaleDateString(),
      Description: exp.description,
      Category: exp.category,
      Amount: exp.amount,
      Status: exp.paymentStatus === 'pending' ? 'Pending' : 'Paid',
      'Payment Mode': exp.paymentMode || '-',
      'Created By': exp.createdBy?.username || 'Unknown'
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Expenses');
    XLSX.writeFile(wb, `expenses_${filterPeriod}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <Card className="border-0 shadow-sm rounded-4">
      <Card.Header className="bg-white border-0 pt-4 pb-0 px-4 d-flex justify-content-between align-items-center">
        <div>
          <h4 className="mb-1 text-primary"><i className="fas fa-wallet me-2"></i>Expense Tracker</h4>
          <p className="text-muted mb-0 small">Manage and track your day-to-day expenses</p>
        </div>
        <Button variant="primary" className="rounded-pill px-4" onClick={() => {
          setIsEditing(false);
          setSelectedExpenseId(null);
          setForm({ description: '', amount: '', category: PREDEFINED_CATEGORIES[0], paymentStatus: 'paid', paymentMode: 'Cash', date: new Date().toISOString().slice(0, 10) });
          setShowAddModal(true);
        }}>
          <i className="fas fa-plus me-2"></i>Add Expense
        </Button>
      </Card.Header>
      
      <Card.Body className="p-4">
        <Row className="mb-4 align-items-end">
          <Col md={3}>
            <Form.Group>
              <Form.Label className="text-muted small fw-bold text-uppercase">Filter Period</Form.Label>
              <Form.Select 
                value={filterPeriod} 
                onChange={(e) => {
                  setFilterPeriod(e.target.value);
                  setCurrentPage(1);
                }}
                className="rounded-3 shadow-sm border-0 bg-light"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
                <option value="all">All Time</option>
                <option value="custom">Custom Range</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          {filterPeriod === 'daily' && (
            <Col md={2}>
              <Form.Group>
                <Form.Label className="text-muted small fw-bold text-uppercase">Select Date</Form.Label>
                <Form.Control type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="rounded-3" />
              </Form.Group>
            </Col>
          )}
          
          {filterPeriod === 'monthly' && (
            <Col md={2}>
              <Form.Group>
                <Form.Label className="text-muted small fw-bold text-uppercase">Select Month</Form.Label>
                <Form.Control type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="rounded-3" />
              </Form.Group>
            </Col>
          )}

          {filterPeriod === 'yearly' && (
            <Col md={2}>
              <Form.Group>
                <Form.Label className="text-muted small fw-bold text-uppercase">Select Year</Form.Label>
                <Form.Control type="number" min="2000" max="2100" value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="rounded-3" />
              </Form.Group>
            </Col>
          )}

          {filterPeriod === 'custom' && (
            <>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="text-muted small fw-bold text-uppercase">Start Date</Form.Label>
                  <Form.Control type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} className="rounded-3" />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="text-muted small fw-bold text-uppercase">End Date</Form.Label>
                  <Form.Control type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} className="rounded-3" />
                </Form.Group>
              </Col>
            </>
          )}

          <Col md={['custom'].includes(filterPeriod) ? 5 : ['daily', 'monthly', 'yearly'].includes(filterPeriod) ? 7 : 9} className="text-end">
            <h5 className="mb-0 me-4 d-inline-block text-secondary">
              Total: <span className="text-primary fw-bold fs-4">₹{totalExpenses.toFixed(2)}</span>
            </h5>
            <Button variant="outline-danger" size="sm" className="me-2 rounded-pill px-3" onClick={exportToPDF} disabled={expenses.length === 0}>
              <i className="fas fa-file-pdf me-1"></i> PDF
            </Button>
            <Button variant="outline-success" size="sm" className="rounded-pill px-3" onClick={exportToExcel} disabled={expenses.length === 0}>
              <i className="fas fa-file-excel me-1"></i> Excel
            </Button>
          </Col>
        </Row>
        <Row className="mb-4 align-items-end">
          <Col md={3}>
            <Form.Group>
              <Form.Label className="text-muted small fw-bold text-uppercase">Status</Form.Label>
              <Form.Select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-3 shadow-sm border-0 bg-light"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="text-muted small fw-bold text-uppercase">Mode</Form.Label>
              <Form.Select 
                value={modeFilter} 
                onChange={(e) => setModeFilter(e.target.value)}
                className="rounded-3 shadow-sm border-0 bg-light"
              >
                <option value="all">All Modes</option>
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Card">Card</option>
                <option value="Cheque">Cheque</option>
                <option value="Other">Other</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="text-muted small fw-bold text-uppercase">Sort By</Form.Label>
              <Form.Select 
                value={sortOption} 
                onChange={(e) => setSortOption(e.target.value)}
                className="rounded-3 shadow-sm border-0 bg-light"
              >
                <option value="date-desc">Newest First</option>
                <option value="date-asc">Oldest First</option>
                <option value="amount-desc">Amount: High to Low</option>
                <option value="amount-asc">Amount: Low to High</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <>
            <div className="table-responsive rounded-4 shadow-sm border">
              <Table hover className="mb-0 align-middle">
                <thead className="bg-light">
                  <tr>
                    <th className="py-3 px-4 border-0">Date</th>
                    <th className="py-3 px-4 border-0">Description</th>
                    <th className="py-3 px-4 border-0">Category</th>
                    <th className="py-3 px-4 border-0 text-end">Amount</th>
                    <th className="py-3 px-4 border-0">Status</th>
                    <th className="py-3 px-4 border-0">Mode</th>
                    <th className="py-3 px-4 border-0">Added By</th>
                    <th className="py-3 px-4 border-0 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedExpenses.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-5 text-muted">
                        No expenses found for this period.
                      </td>
                    </tr>
                  ) : (
                    paginatedExpenses.map((exp: any) => (
                      <tr key={exp.id}>
                        <td className="px-4 text-nowrap">{new Date(exp.date).toLocaleDateString()}</td>
                        <td className="px-4 fw-medium">{exp.description}</td>
                        <td className="px-4">
                          <Badge bg="light" text="dark" className="border fw-normal px-2 py-1">
                            {exp.category}
                          </Badge>
                        </td>
                        <td className="px-4 text-end fw-bold text-primary">
                          ₹{exp.amount.toFixed(2)}
                        </td>
                        <td className="px-4">
                          <Form.Select 
                            size="sm"
                            value={exp.paymentStatus || 'paid'}
                            onChange={(e) => handleStatusChange(exp.id, e.target.value)}
                            className={`border-0 fw-bold ${exp.paymentStatus === 'pending' ? 'text-warning' : 'text-success'}`}
                            style={{ minWidth: '100px', backgroundColor: 'transparent' }}
                          >
                            <option value="pending" className="text-warning">Pending</option>
                            <option value="paid" className="text-success">Paid</option>
                          </Form.Select>
                        </td>
                        <td className="px-4">
                          <Form.Select
                            size="sm"
                            value={exp.paymentMode || ''}
                            onChange={(e) => handleModeChange(exp.id, e.target.value)}
                            className="border-0 bg-transparent"
                            style={{ minWidth: '110px' }}
                          >
                            <option value="">Select Mode</option>
                            <option value="Cash">Cash</option>
                            <option value="UPI">UPI</option>
                            <option value="Bank Transfer">Bank Transfer</option>
                            <option value="Card">Card</option>
                            <option value="Cheque">Cheque</option>
                            <option value="Other">Other</option>
                          </Form.Select>
                        </td>
                        <td className="px-4 text-muted small">
                          {exp.createdBy?.username || 'Unknown'}
                        </td>
                        <td className="px-4 text-center">
                          {isAdminManager && (
                            <Button variant="link" className="text-primary p-0 me-3" onClick={() => handleEditClick(exp)}>
                              <i className="fas fa-edit"></i>
                            </Button>
                          )}
                          <Button variant="link" className="text-danger p-0" onClick={() => handleDeleteExpense(exp.id)}>
                            <i className="fas fa-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
            
            {totalPages > 1 && (
              <div className="mt-4">
                <AdminPagination
                  currentPage={currentPage}
                  totalItems={expenses.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </Card.Body>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="text-primary"><i className={`fas ${isEditing ? 'fa-edit' : 'fa-plus-circle'} me-2`}></i>{isEditing ? 'Edit Expense' : 'Add Expense'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddExpense}>
            <Form.Group className="mb-3">
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                required
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
                className="rounded-3 shadow-sm border-0 bg-light"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                required
                placeholder="E.g., Bought printer ink"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                className="rounded-3 shadow-sm border-0 bg-light"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                required
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                className="rounded-3 shadow-sm border-0 bg-light"
              >
                {PREDEFINED_CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label>Amount (₹)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                required
                placeholder="0.00"
                value={form.amount}
                onChange={e => setForm({ ...form, amount: e.target.value })}
                className="rounded-3 shadow-sm border-0 bg-light"
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    required
                    value={form.paymentStatus}
                    onChange={e => setForm({ ...form, paymentStatus: e.target.value })}
                    className="rounded-3 shadow-sm border-0 bg-light"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>Payment Mode</Form.Label>
                  <Form.Select
                    required
                    value={form.paymentMode}
                    onChange={e => setForm({ ...form, paymentMode: e.target.value })}
                    className="rounded-3 shadow-sm border-0 bg-light"
                  >
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Card">Card</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <div className="d-grid">
              <Button variant="primary" type="submit" className="rounded-pill py-2">
                {isEditing ? 'Update Expense' : 'Save Expense'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Card>
  );
};

export default ExpenseTracker;
